// ---------------------------------------------------------------------------
// CreateTestSetDialog — Dialog to create a new test set.
//
// User selects a parent dataset (fetched from /datasets/search), gives a name
// and optional description. Type, access, and sensitivity are inherited from
// the selected parent dataset.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Combobox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Option,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import type { Dataset } from '../../../services/dataHubService.interface.js';
import { DataHubService } from '../../../services/dataHubService.js';
import {
  AccessCategory,
  SensitivityLabel,
} from '../../../services/testSetService.interface.js';
import { TestSetService } from '../../../services/testSetService.js';
import { withCorrelationId } from '../../../services/usageContext.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';

import type { CreateTestSetDialogProps } from './CreateTestSetDialog.interface.js';
import { useStyles } from './CreateTestSetDialog.style.js';

const SEARCH_DEBOUNCE_MS = 300;

const ACCESS_CATEGORY_BY_LEVEL: Record<string, AccessCategory> = {
  Public: AccessCategory.Public,
  Restricted: AccessCategory.Restricted,
};

export function CreateTestSetDialog({
  open,
  onClose,
  onCreated,
}: CreateTestSetDialogProps) {
  const styles = useStyles();
  const client = useDataHubClient();
  const dataHubService = useMemo(() => new DataHubService(client), [client]);
  const testSetService = useMemo(() => new TestSetService(client), [client]);
  const scenarioFactory = useScenarioFactory();

  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [datasetSearchQuery, setDatasetSearchQuery] = useState('');
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch datasets when dialog opens or search query changes (debounced)
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setIsLoadingDatasets(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      dataHubService
        .searchDatasets({
          query: datasetSearchQuery || undefined,
          pageSize: 50,
          includeCount: false,
        })
        .then((result) => {
          if (cancelled) return;
          setDatasets(result.items);
          setIsLoadingDatasets(false);
        })
        .catch(() => {
          if (cancelled) return;
          setIsLoadingDatasets(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, dataHubService, datasetSearchQuery]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDataset(null);
      setName('');
      setDescription('');
      setError(null);
      setDatasetSearchQuery('');
    }
  }, [open]);

  const datasetMap = useMemo(() => {
    const map = new Map<string, Dataset>();
    for (const ds of datasets) {
      map.set(ds.id, ds);
    }
    return map;
  }, [datasets]);

  const handleDatasetSelect = useCallback(
    (_e: unknown, data: { optionValue?: string }) => {
      const dsId = data.optionValue;
      if (dsId) {
        const ds = datasetMap.get(dsId) ?? null;
        setSelectedDataset(ds);
      }
    },
    [datasetMap],
  );

  const handleSubmit = useCallback(async () => {
    if (selectedDataset === null || name.trim() === '') return;

    setIsSubmitting(true);
    setError(null);

    const scenario = scenarioFactory.newScenario(ScenarioName.TestsetCreate);
    try {
      const accessCategory =
        ACCESS_CATEGORY_BY_LEVEL[selectedDataset.accessLevel ?? 'Public'] ??
        AccessCategory.Public;
      const sensitivityLabel = SensitivityLabel.General;

      const testSet = await testSetService.createTestSet(
        {
          dataSetId: selectedDataset.id,
          name: name.trim(),
          sensitivityLabel,
          accessCategory,
          description: description.trim() || undefined,
        },
        { extras: withCorrelationId(scenario.queryId) },
      );
      scenario.complete('success');

      onCreated(testSet);
      onClose();
    } catch (err) {
      const classification = classifyScenarioError(err);
      scenario.complete(classification.status, classification.data);
      setError(
        err instanceof Error ? err.message : 'Failed to create test set',
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedDataset,
    name,
    description,
    testSetService,
    onCreated,
    onClose,
    scenarioFactory,
  ]);

  const canSubmit = selectedDataset !== null && name.trim() !== '';

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>New Test Set</DialogTitle>
          <DialogContent>
            <div className={styles.form}>
              {/* Parent Dataset selector */}
              <div className={styles.fieldGroup}>
                <span className={styles.label}>
                  Parent Dataset <span className={styles.required}>*</span>
                </span>
                {isLoadingDatasets && datasets.length === 0 ? (
                  <div className={styles.loadingDatasets}>
                    <Spinner size="small" label="Loading datasets..." />
                  </div>
                ) : (
                  <Combobox
                    placeholder="Search datasets..."
                    value={
                      selectedDataset
                        ? selectedDataset.name
                        : datasetSearchQuery
                    }
                    selectedOptions={
                      selectedDataset ? [selectedDataset.id] : []
                    }
                    onOptionSelect={handleDatasetSelect}
                    onChange={(e) => {
                      setDatasetSearchQuery(e.target.value);
                      if (selectedDataset) setSelectedDataset(null);
                    }}
                    aria-label="Select parent dataset"
                    freeform
                  >
                    {datasets.map((ds) => (
                      <Option key={ds.id} value={ds.id} text={ds.name}>
                        {ds.name}
                      </Option>
                    ))}
                  </Combobox>
                )}
              </div>

              {/* Name */}
              <div className={styles.fieldGroup}>
                <span className={styles.label}>
                  Name <span className={styles.required}>*</span>
                </span>
                <Input
                  value={name}
                  onChange={(_, data) => setName(data.value)}
                  placeholder="Test set name"
                  aria-label="Test set name"
                />
              </div>

              {/* Description */}
              <div className={styles.fieldGroup}>
                <span className={styles.label}>Description</span>
                <Textarea
                  value={description}
                  onChange={(_, data) => setDescription(data.value)}
                  placeholder="Optional description"
                  aria-label="Test set description"
                  resize="vertical"
                />
              </div>

              {error !== null && (
                <Body1 className={styles.error}>{error}</Body1>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              disabled={isSubmitting}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!canSubmit || isSubmitting}
              icon={isSubmitting ? <Spinner size="tiny" /> : undefined}
              onClick={() => {
                void handleSubmit();
              }}
            >
              Create
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
